import type {
  BotConversation,
  BotConversationState,
  HandoffTrigger,
  HandoffTriggerKind,
} from '../../types/leadBot';

// SMS Lead Bot — pure view logic for the approval queue. PURE.
//
// ⚠️ The queue IS the Phase 3 conversation viewer, not a stopgap. So the
// ordering and the labelling here are built to be worth keeping: an AM working
// the morning queue and someone reading a transcript three months later want
// the same list, sorted the same way.

export type QueueFilter = {
  /** Empty means every state. */
  states: BotConversationState[];
  /** Only conversations with a draft waiting on a human. */
  pendingOnly: boolean;
  /** Only conversations flagged for a person. */
  handoffOnly: boolean;
  accountId: string | null;
  search: string;
};

export const EMPTY_QUEUE_FILTER: QueueFilter = {
  states: [],
  pendingOnly: false,
  handoffOnly: false,
  accountId: null,
  search: '',
};

/**
 * ⚠️ THE MORNING ORDER, and it is not "newest first". What an AM needs first is
 * what is BLOCKED ON THEM: a draft waiting for approval, then a conversation
 * flagged for a person, then everything else by recency. A pure recency sort
 * buries the two things the queue exists for under whatever moved last.
 */
export function sortConversations(
  conversations: readonly BotConversation[],
  pendingIds: ReadonlySet<string>,
): BotConversation[] {
  return [...conversations].sort((a, b) => {
    const aPending = pendingIds.has(a.id) ? 0 : 1;
    const bPending = pendingIds.has(b.id) ? 0 : 1;
    if (aPending !== bPending) return aPending - bPending;
    const aHandoff = a.state === 'handoff' ? 0 : 1;
    const bHandoff = b.state === 'handoff' ? 0 : 1;
    if (aHandoff !== bHandoff) return aHandoff - bHandoff;
    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
  });
}

export function filterConversations(
  conversations: readonly BotConversation[],
  filter: QueueFilter,
  pendingIds: ReadonlySet<string>,
): BotConversation[] {
  const search = filter.search.trim().toLowerCase();
  return conversations.filter((c) => {
    if (filter.states.length > 0 && !filter.states.includes(c.state)) return false;
    if (filter.pendingOnly && !pendingIds.has(c.id)) return false;
    if (filter.handoffOnly && c.state !== 'handoff') return false;
    if (filter.accountId && c.accountId !== filter.accountId) return false;
    if (search !== '') {
      const haystack = [c.leadName, c.leadPhone, c.accountName ?? '', c.campaign ?? '']
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

export const STATE_LABELS: Record<BotConversationState, string> = {
  active: 'Active',
  handoff: 'Needs a person',
  closed: 'Closed',
  stopped: 'Opted out',
};

export const STATE_VARIANTS: Record<BotConversationState, 'success' | 'warning' | 'neutral' | 'danger'> = {
  active: 'success',
  handoff: 'warning',
  closed: 'neutral',
  stopped: 'danger',
};

export const TRIGGER_LABELS: Record<HandoffTriggerKind, string> = {
  // ⚠️ Named for what it IS, not softened. An AM seeing this must know the bot
  // was forbidden from answering, not that it merely chose not to.
  compliance: 'Licence / towing / weight — must not be answered',
  human_requested: 'Asked for a person',
  price_negotiation: 'Price negotiation',
  finance: 'Finance',
  trade_in: 'Trade-in',
  complaint: 'Complaint',
  booking_ready: 'Ready to book',
  unknown_to_kb: 'Not covered by the knowledge base',
  stalled: 'Going in circles',
  manual: 'Handed over by an account manager',
  cancellation_billing: 'Cancellation / refund / billing',
  injury_medical: 'Injury / medical',
  legal: 'Legal',
  safety_incident: 'Safety or incident in the club',
  media_corporate: 'Media / franchise / corporate',
  faq_handover: 'Club-marked handover question',
  distress: 'POSSIBLE DISTRESS - automation stopped',
  opt_out: 'Asked us to stop messaging',
  price_unverified: 'Reply held - unlisted price',
  not_sure: 'Assistant was not sure',
  reply_cap: 'Reply limit reached',
  hourly_cap: 'Hourly reply limit reached',
  drafting_error: "The assistant couldn't reply - something went wrong",
  automated_counterparty: 'Looks like an auto-reply - assistant stopped',
  booking_failed: 'Booking needs a person - the assistant could not lock the time in',
};

export const SOURCE_LABELS: Record<string, string> = {
  rule: 'detected by our own checks',
  model: 'flagged by the assistant',
  manual: 'flagged by a person',
};

/** The single most severe trigger, for a row badge. Compliance always wins. */
export function leadTrigger(
  triggers: readonly HandoffTrigger[],
): HandoffTrigger | null {
  return triggers.find((t) => t.kind === 'compliance') ?? triggers[0] ?? null;
}

/** "2 minutes ago" style, but absolute past a day — a transcript needs the date. */
export function formatWhen(iso: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  const delta = nowMs - ms;
  if (delta < 60_000) return 'just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return new Date(ms).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The URL that opens one conversation in the queue.
 *
 * ⚠️ ONE DEFINITION, because the report's compliance list links here and the
 * queue reads the param — the whole value of that list is that every row opens
 * the transcript, and a mismatched param name would leave it landing on an
 * unfiltered queue with nothing selected. That failure looks like a working
 * link.
 */
export const CONVERSATION_PARAM = 'c';

export function conversationHref(conversationId: string): string {
  return `/kyckconnect/approvals?${CONVERSATION_PARAM}=${encodeURIComponent(conversationId)}`;
}

// ---------------------------------------------------------------------------
// Run U: what the UI says about a handed-over conversation / the page's mode.
// ⚠️ Since Run R a FITNESS assistant keeps replying after a handover, so "quiet until you hand it back"
// is false there. Only distress, an opt-out and the reply limit actually stop it.
// ---------------------------------------------------------------------------

export function handoverStatusNote(
  conversation: Pick<BotConversation, 'handoffTriggers'> & { distressAt?: string | null },
  opts: { fitness: boolean; assistantName: string },
): string {
  const name = opts.assistantName || 'The assistant';
  if (conversation.distressAt) return 'the assistant is quiet on this conversation until you hand it back';
  if (conversation.handoffTriggers.some((t) => t.kind === 'reply_cap')) {
    return `Reply limit reached - ${name} has stopped. Raise the limit in Bot settings, then hand back`;
  }
  if (opts.fitness) return `Staff alerted - ${name} is still replying`;
  return 'the assistant is quiet on this conversation until you hand it back';
}

export function pageIntroText(mode: 'draft' | 'autonomous' | null): string {
  if (mode === 'autonomous') {
    return 'This bot replies on its own. A draft only waits here when an automatic send did not go out.';
  }
  if (mode === 'draft') {
    return 'Every reply is drafted by the assistant and waits here for a person to approve it. Nothing sends on its own.';
  }
  return 'Pick a conversation to see how its bot replies: draft-mode bots wait for approval, autonomous bots send on their own.';
}
